import Vue, { DirectiveBinding } from 'vue'

interface SpotlightDirectiveOptions {
  isEnabled?: boolean,
  offset?: {
    x?: number,
    y?: number,
  },
  padding?: number,
  opacity?: number,
}

interface MethodOptions {
  offsetX?: number,
  offsetY?: number,
  padding?: number,
  opacity?: number,
}

let overlayElement: SVGElement | null = null
const activeElements: HTMLElement[] = []

function addOverlay(
  el: HTMLElement,
  { offsetX, offsetY, padding, opacity }: MethodOptions = {}
) {
  const overlaySvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  overlaySvg.setAttribute('width', '100%')
  overlaySvg.setAttribute('height', '100%')
  overlaySvg.classList.add('spotlight')

  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
  const mask = document.createElementNS('http://www.w3.org/2000/svg', 'mask')
  mask.setAttribute('id', 'mask')

  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
  rect.setAttribute('x', '0')
  rect.setAttribute('y', '0')
  rect.setAttribute('width', '100%')
  rect.setAttribute('height', '100%')
  rect.setAttribute('fill', '#fff')

  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
  circle.setAttribute('cx', '0')
  circle.setAttribute('cy', '0')
  circle.setAttribute('r', '0')

  mask.appendChild(rect)
  mask.appendChild(circle)

  defs.appendChild(mask)
  overlaySvg.appendChild(defs)

  const overlayRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
  overlayRect.setAttribute('x', '0')
  overlayRect.setAttribute('y', '0')
  overlayRect.setAttribute('width', '100%')
  overlayRect.setAttribute('height', '100%')
  overlayRect.setAttribute('mask', 'url(#mask)')
  overlayRect.setAttribute('fill-opacity', String(opacity))

  overlaySvg.appendChild(overlayRect)

  updateOverlay(el, { offsetX, offsetY, padding }) // Initial update

  // Store the overlay element
  overlayElement = overlaySvg

  // Add SVG overlay to the document
  document.body.appendChild(overlaySvg)

  // Keep track of the elements which currently use this directive
  activeElements.push(el)
}

function updateOverlay(
  el: HTMLElement,
  { offsetX, offsetY, padding }: MethodOptions = {}
) {
  if (!document.body.contains(overlayElement)) {
    return
  }

  const elementRect = el.getBoundingClientRect()
  const centerX = elementRect.left + offsetX + elementRect.width / 2
  const centerY = elementRect.top + offsetY + elementRect.height / 2
  const radius = Math.max(elementRect.width, elementRect.height) / 2 + padding

  // Update SVG overlay props
  overlayElement.querySelector('circle')?.setAttribute('cx', String(centerX))
  overlayElement.querySelector('circle')?.setAttribute('cy', String(centerY))
  overlayElement.querySelector('circle')?.setAttribute('r', String(radius))
}

function removeOverlay(el: HTMLElement) {
  const index = activeElements.indexOf(el)

  if (index > -1) {
    activeElements.splice(index, 1)
  }

  if (overlayElement && activeElements.length === 0) {
    overlayElement.remove()
    overlayElement = null
  }
}

const cutoutDirective = {
  inserted(el: HTMLElement, binding: DirectiveBinding<SpotlightDirectiveOptions>) {
    const {
      isEnabled = false,
      offset: { x: offsetX = 0, y: offsetY = 0 } = {},
      padding = 0,
      opacity = 0.2
    } = binding.value || {}

    if (!isEnabled) {
      return
    }

    // Create SVG overlay
    if (!document.body.contains(overlayElement)) {
      addOverlay(el, { offsetX, offsetY, padding, opacity })
    }

    // Watch for position changes
    const observer = new MutationObserver(() => updateOverlay(el, { offsetX, offsetY, padding }))
    observer.observe(el, { attributes: true })
  },
  async componentUpdated(el: HTMLElement, binding: DirectiveBinding<SpotlightDirectiveOptions>) {
    const {
      isEnabled,
      offset: { x: offsetX = 0, y: offsetY = 0 } = {},
      padding = 0,
      opacity = 0.2
    } = binding.value || {}

    if (!isEnabled) {
      return removeOverlay(el)
    } else if (!document.body.contains(overlayElement)) {
      addOverlay(el, { offsetX, offsetY, padding, opacity })
    }

    await Vue.nextTick()

    updateOverlay(el, { offsetX, offsetY, padding })
  },
  unbind(el: HTMLElement) {
    removeOverlay(el)
  }
}

Vue.directive('spotlight', cutoutDirective)
